import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LandingPage from './components/LandingPage';
import Hero from './components/Hero';
import ProfileInput from './components/ProfileInput';
import Questionnaire from './components/Questionnaire';
import Loading from './components/Loading';
import Dashboard from './components/Dashboard';
import SEO from './components/SEO';
import BlogPage from './components/BlogPage';
import AuthGate from './components/AuthGate';
import SampleAuditViewer from './components/SampleAuditViewer';
import { useAuth } from './context/AuthContext';
import './App.css';
import maestroApiService from './services/maestroApiService';
import { getScoresAndOptimizations, getStoredAnalysis } from './services/aiService';
import consoleLogger from './utils/consoleLogger';
import { cleanTextInObject } from './utils/textCleaner';

// Feature flag to enable backend AI scoring/optimization wiring
const ENABLE_AI_SCORING = true; // enabled to use AI path

function App() {
  const [currentStep, setCurrentStep] = useState('hero');
  const [profileUrl, setProfileUrl] = useState('');
  const [questionnaireData, setQuestionnaireData] = useState({});
  const [auditResults, setAuditResults] = useState(null);
  const [scrapedProfileData, setScrapedProfileData] = useState(null);
  const [airtableRecordId, setAirtableRecordId] = useState(null);
  const [prefillAnswers, setPrefillAnswers] = useState(null);
  const [isScrapingComplete, setIsScrapingComplete] = useState(false);
  const [scrapingStarted, setScrapingStarted] = useState(false);
  // AI wiring state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const aiAbortRef = useRef(null);
  // Ensure loading completion logic runs only once per run
  const loadingCompleteHandled = useRef(false);
  
  // Auth context (credits removed - using Razorpay payment instead)
  const { user } = useAuth();
  
  // Simple URL-based caching
  const scrapedDataCache = useRef(new Map());
  const questionnaireCache = useRef(new Map());
  const activeScrapingUrls = useRef(new Set());

  // Progress steps (header hidden on hero)
  const steps = ['profile', 'questionnaire', 'loading', 'dashboard'];

  // Initialize console logging on app mount
  useEffect(() => {
    consoleLogger.startCapture();
    console.log('🚀 [APP] Application started - Console logging enabled');
    
    return () => {
      consoleLogger.stopCapture();
    };
  }, []);

  // Simple function to check if URL has been scraped
  const hasScrapedData = (url) => {
    return scrapedDataCache.current.has(url);
  };

  // Get cached scraped data
  const getCachedScrapedData = (url) => {
    return scrapedDataCache.current.get(url) || null;
  };

  // Cache scraped data
  const setCachedScrapedData = (url, data) => {
    scrapedDataCache.current.set(url, data);
    const urlKey = btoa(url).replace(/[^a-zA-Z0-9]/g, '');
    sessionStorage.setItem(`scrapedProfileData_${urlKey}`, JSON.stringify(data));
    sessionStorage.setItem('currentProfileUrl', url);
  };

  // Deduplication no longer needed with MongoDB (MongoDB handles this automatically)
  const deduplicateProfileRecords = async (url) => {
    // MongoDB automatically handles duplicate prevention via upsert
    console.log('✅ [MONGODB] Duplicate prevention handled automatically');
    return;
  };

  // Simple scraping function
  const startScraping = async (url) => {
    console.log(`� startScraping called for: ${url}`);
    
    // Check if data already exists in cache
    if (hasScrapedData(url)) {
      console.log(`✅ Using cached data for: ${url}`);
      const cachedData = getCachedScrapedData(url);
      setScrapedProfileData(cachedData);
      setIsScrapingComplete(true);
      return cachedData;
    }
    
    // Check if scraping is already in progress for this URL
    if (activeScrapingUrls.current.has(url)) {
      console.log(`⏳ Scraping already in progress for: ${url}`);
      return null;
    }

    // Mark URL as being scraped
    activeScrapingUrls.current.add(url);
    setScrapingStarted(true);
    setIsScrapingComplete(false);

    try {
      console.log(`🔍 Starting scraping for: ${url}`);
      const profileData = await maestroApiService.scrapeLinkedInProfile(url);
      
      // Extract and store MongoDB record ID if present
      if (profileData._mongoRecordId) {
        console.log('📝 MongoDB Record ID from scraping:', profileData._mongoRecordId);
        setAirtableRecordId(profileData._mongoRecordId);
      }
      
      // Cache the data
      setCachedScrapedData(url, profileData);
      setScrapedProfileData(profileData);
      setIsScrapingComplete(true);
      
      console.log(`✅ Scraping completed for: ${url}`);
      return profileData;
      
    } catch (error) {
      console.error(`❌ Scraping failed for: ${url}`, error);
      setIsScrapingComplete(true);
      throw error;
    } finally {
      // Remove from active scraping
      activeScrapingUrls.current.delete(url);
    }
  };

  // Questionnaire caching functions
  const getCachedQuestionnaire = (url) => {
    return questionnaireCache.current.get(url) || null;
  };
  
  const setCachedQuestionnaire = (url, data) => {
    questionnaireCache.current.set(url, data);
  };
  
  const fetchQuestionnaireData = async (url) => {
    // Check cache first
    const cached = getCachedQuestionnaire(url);
    if (cached) {
      console.log(`� Using cached questionnaire data for: ${url}`);
      return cached;
    }
    
    console.log(`📡 Fetching questionnaire data for: ${url}`);
    try {
      const apiUrl = process.env.NODE_ENV === 'production' ? `/api/get-questionnaire?url=${encodeURIComponent(url)}` : `http://localhost:3001/api/get-questionnaire?url=${encodeURIComponent(url)}`;
      const res = await fetch(apiUrl);
      const data = await res.json();
      
      // Cache the result
      setCachedQuestionnaire(url, data);
      return data;
    } catch (e) {
      console.error('Failed to fetch questionnaire:', e);
      return { success: false, exists: false, answers: {} };
    }
  };


  // Internal function to actually proceed with URL submission after credit check
  const proceedWithUrlSubmit = (url, scrapedData = null) => {
    console.log(`📝 proceedWithUrlSubmit called with URL: ${url}`);
    setProfileUrl(url);
    setPrefillAnswers(null);
    setIsScrapingComplete(false);
    setScrapingStarted(false);
    loadingCompleteHandled.current = false;
    
    if (scrapedData) {
      // Pre-scraped data provided
      setCachedScrapedData(url, scrapedData);
      setScrapedProfileData(scrapedData);
      setIsScrapingComplete(true);
    } else {
      // Start scraping for new user
      startScraping(url);
    }
    
    setCurrentStep('questionnaire');
  };

  const handleUrlSubmit = (url, scrapedData = null) => {
    console.log(`📝 handleUrlSubmit called with URL: ${url}`);
    // Credits removed - proceed directly with URL submission
    proceedWithUrlSubmit(url, scrapedData);
  };

  const handleEditPrevious = (url) => {
    console.log(`✏️ handleEditPrevious called with URL: ${url}`);
    // Credits removed - proceed directly with edit
    proceedWithEditPrevious(url);
  };

  // Internal function to actually proceed with edit after credit confirmation
  const proceedWithEditPrevious = async (url) => {
    console.log(`✏️ proceedWithEditPrevious called with URL: ${url}`);
    setProfileUrl(url);
    setIsScrapingComplete(false);
    setScrapingStarted(false);
    loadingCompleteHandled.current = false;
    
    try {
      // Fetch questionnaire data for editing
      const data = await fetchQuestionnaireData(url);
      if (data && data.success && data.exists && data.answers && typeof data.answers === 'object') {
        setPrefillAnswers(data.answers);
      } else {
        setPrefillAnswers({});
      }
      
      // Load existing scraped data if available but don't start new scraping
      if (hasScrapedData(url)) {
        console.log(`✅ Loading existing scraped data for editing: ${url}`);
        const cachedData = getCachedScrapedData(url);
        setScrapedProfileData(cachedData);
        setIsScrapingComplete(true);
      }
      // Note: No scraping here - will happen when user clicks Generate Analysis
      
    } catch (e) {
      console.error('Failed to fetch previous questionnaire:', e);
      setPrefillAnswers({});
    }
    
    setCurrentStep('questionnaire');
  };

  const handleQuestionnaireComplete = async (data) => {
    setQuestionnaireData(data);
    loadingCompleteHandled.current = false;
    
    // Do NOT store questionnaire yet. We will store it after scraping completes
    // to ensure profile data is already saved in Airtable.

    // Start scraping when user completes questionnaire (for both new and edit users)
    if (!hasScrapedData(profileUrl)) {
      console.log(`🔄 Starting scraping after questionnaire completion for: ${profileUrl}`);
      startScraping(profileUrl);
    } else {
      // Already have scraped data, mark as complete
      setIsScrapingComplete(true);
    }

    // Redirect to loading
    setCurrentStep('loading');
  };

  const checkScrapingStatus = () => {
    return {
      isComplete: isScrapingComplete,
      hasStarted: scrapingStarted
    };
  };

  const handleLoadingComplete = () => {
    if (loadingCompleteHandled.current) {
      console.log('⚠️ handleLoadingComplete called again; ignoring (already handled).');
      return;
    }
    loadingCompleteHandled.current = true;
    // Now that scraping has completed (and profile stored in Airtable),
    // store the questionnaire answers and then proceed to dashboard
    (async () => {
      try {
        console.log('📝 Storing questionnaire data to MongoDB...');
        const storeApiUrl = process.env.NODE_ENV === 'production' ? '/api/store-questionnaire' : 'http://localhost:3001/api/store-questionnaire';
        const response = await fetch(storeApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            profileUrl, 
            questionnaire: questionnaireData,
            airtableRecordId: airtableRecordId // Pass the exact record ID
          })
        });
        
        const result = await response.json();
        if (result.success && result.updated) {
          console.log('✅ Successfully merged questionnaire into existing profile record!');
        } else if (result.success && !result.updated) {
          console.warn('⚠️ Created new record for questionnaire (scraped profile may not have been saved yet)');
        }
      } catch (e) {
        console.error('Failed to store questionnaire after scraping:', e);
      } finally {
        if (ENABLE_AI_SCORING) {
          // Attempt AI scoring path; fallback to local if it fails
          try {
            // Cancel any in-flight AI call
            if (aiAbortRef.current && aiLoading) {
              aiAbortRef.current.abort();
            }

            const controller = new AbortController();
            aiAbortRef.current = controller;
            setAiLoading(true);
            setAiError(null);

            // Gather scraped data
            const aiUrlKey = btoa(profileUrl).replace(/[^a-zA-Z0-9]/g, '');
            const scrapedData = scrapedProfileData ||
              getCachedScrapedData(profileUrl) ||
              JSON.parse(sessionStorage.getItem(`scrapedProfileData_${aiUrlKey}`) || 'null');

            const ai = await getScoresAndOptimizations({
              profileUrl,
              scrapedData,
              questionnaire: questionnaireData,
              options: { signal: controller.signal }
            });
            // Debug: observe raw AI output
            try { console.log('🤖 [AI] Raw response:', ai); } catch (_) {}

            // Map AI response into existing results structure expected by Dashboard
            const results = buildResultsFromAi(ai, scrapedData, questionnaireData);
            
            // Clean em-dashes from AI-generated content
            const cleanedResults = cleanTextInObject(results);
            try { console.log('🧭 [AI] Mapped results (em-dashes cleaned):', cleanedResults); } catch (_) {}

            sessionStorage.setItem(`auditResults_${aiUrlKey}`, JSON.stringify(cleanedResults));
            sessionStorage.setItem(`questionnaireData_${aiUrlKey}`, JSON.stringify(questionnaireData));
            setAuditResults(cleanedResults);
          } catch (err) {
            if (err?.name === 'AbortError') {
              // Silent ignore; another flow likely superseded this call
            } else {
              const errorMessage = err?.message || String(err);
              console.error('AI scoring failed, falling back to local generation:', err);

              // Enhanced error handling for both dev and production
              if (errorMessage.includes('timeout') || errorMessage.includes('network') || errorMessage.includes('fetch')) {
                setAiError(`Network issue: ${errorMessage}. Using fallback analysis.`);
              } else if (errorMessage.includes('Invalid model output') || errorMessage.includes('Non-retryable')) {
                setAiError('AI service temporarily unavailable. Using fallback analysis.');
              } else if (errorMessage.includes('Memory limit exceeded') || errorMessage.includes('memory')) {
                setAiError('Profile too complex for AI analysis. Using fallback analysis.');
              } else if (errorMessage.includes('Serverless function error') || errorMessage.includes('FUNCTION_INVOCATION')) {
                setAiError('Platform issue detected. Using fallback analysis.');
              } else if (errorMessage.includes('Service temporarily unavailable')) {
                setAiError('AI service overloaded. Using fallback analysis.');
              } else {
                setAiError('Using fallback analysis due to service issue.');
              }

              // Always provide fallback results
              generateAuditResults(questionnaireData);
            }
          } finally {
            setAiLoading(false);
            aiAbortRef.current = null;
            
            // Trigger background deduplication to clean up any duplicate records
            deduplicateProfileRecords(profileUrl);
            
            setCurrentStep('dashboard');
          }
        } else {
          // Existing local scoring path
          generateAuditResults(questionnaireData);
          
          // Trigger background deduplication to clean up any duplicate records
          deduplicateProfileRecords(profileUrl);
          
          setCurrentStep('dashboard');
        }
      }
    })();
  };


  const generateSectionsFromScrapedData = (scrapedData, questionnaireData) => {
    // Enhanced section scoring based on scraped data
    const hasScrapedData = scrapedData && typeof scrapedData === 'object';
    
    return {
      profilePicture: {
        score: hasScrapedData && scrapedData.avatar ? Math.floor(Math.random() * 2) + 8 : Math.floor(Math.random() * 4) + 6,
        maxScore: 10,
        current: hasScrapedData && scrapedData.avatar ? 
          "Professional headshot detected with good resolution" : 
          "Professional headshot with good lighting, but background could be more neutral",
        optimized: "Use a crisp, high-resolution head-and-shoulders shot with neutral background. Ensure your face fills 60% of the frame with even lighting and confident expression.",
        priority: 'medium'
      },
      headline: {
        score: hasScrapedData && scrapedData.position ? 
          (scrapedData.position.length > 50 ? Math.floor(Math.random() * 2) + 7 : Math.floor(Math.random() * 3) + 5) :
          Math.floor(Math.random() * 3) + 5,
        maxScore: 10,
        current: hasScrapedData && scrapedData.position ? 
          `Current headline: "${scrapedData.position.substring(0, 100)}${scrapedData.position.length > 100 ? '...' : ''}"` :
          "Generic job title without clear value proposition or quantified results",
        optimized: "Craft a 220-character headline that communicates who you help, problems you solve, and results you deliver. Include keywords and quantified achievements.",
        priority: 'high'
      },
      experience: {
        score: hasScrapedData && scrapedData.experience && scrapedData.experience.length > 0 ? 
          Math.floor(Math.random() * 2) + 7 : Math.floor(Math.random() * 3) + 6,
        maxScore: 10,
        current: hasScrapedData && scrapedData.experience && scrapedData.experience.length > 0 ? 
          `Found ${scrapedData.experience.length} experience entries. Recent role: ${scrapedData.experience[0]?.title || 'N/A'} at ${scrapedData.experience[0]?.company || 'N/A'}` :
          "Job descriptions lack quantified outcomes and strong action verbs",
        optimized: "Add benefit-driven summaries with quantified outcomes. Use strong action verbs and include media attachments as proof of impact.",
        priority: 'high'
      },
      toneAndCohesion: {
        score: Math.floor(Math.random() * 2) + 7, // 7-9
        maxScore: 10,
        current: "Generally consistent tone but some sections use third-person perspective",
        optimized: "Maintain first-person voice throughout. Ensure consistent formatting and language that matches your target executive audience.",
        priority: 'medium'
      },
      about: {
        score: hasScrapedData && scrapedData.about && scrapedData.about.length > 200 ? 
          Math.floor(Math.random() * 2) + 7 : Math.floor(Math.random() * 4) + 4,
        maxScore: 10,
        current: hasScrapedData && scrapedData.about ? 
          `About section found (${scrapedData.about.length} characters): "${scrapedData.about.substring(0, 150)}${scrapedData.about.length > 150 ? '...' : ''}"` :
          "About section exists but lacks clear structure and compelling narrative",
        optimized: "Restructure with 3-part format: hook opener, proof/story body, and clear CTA. Keep under 2,000 characters with natural keyword integration.",
        priority: 'high'
      },
      recommendations: {
        score: hasScrapedData && scrapedData.recommendations_count > 0 ? 
          Math.floor(Math.random() * 3) + 6 : Math.floor(Math.random() * 5) + 3,
        maxScore: 10,
        current: hasScrapedData && scrapedData.recommendations_count > 0 ? 
          `Found ${scrapedData.recommendations_count} recommendations on profile` :
          "Has some recommendations but they lack specificity and recent dates",
        optimized: "Secure minimum 5 recent recommendations from diverse stakeholders with specific details about context, projects, and measurable impact.",
        priority: 'medium'
      },
      banner: {
        score: hasScrapedData && scrapedData.banner_image ? 
          Math.floor(Math.random() * 2) + 7 : Math.floor(Math.random() * 3) + 5,
        maxScore: 10,
        current: hasScrapedData && scrapedData.banner_image ? 
          "Custom banner image detected" : 
          "Default or outdated banner image that doesn't reflect personal brand",
        optimized: "Create high-resolution banner (1584×396px) with brand-consistent colors, clear tagline, and balanced composition that complements your profile picture.",
        priority: 'low'
      },
      honorsAndAwards: {
        score: hasScrapedData && scrapedData.honors_and_awards && scrapedData.honors_and_awards.length > 0 ? 
          Math.floor(Math.random() * 2) + 7 : Math.floor(Math.random() * 4) + 4,
        maxScore: 10,
        current: hasScrapedData && scrapedData.honors_and_awards && scrapedData.honors_and_awards.length > 0 ? 
          `Found ${scrapedData.honors_and_awards.length} awards/honors listed` :
          "No records found",
        optimized: "List only credible, externally validated achievements with awarding body, date, and impact statement. Include hyperlinks for proof.",
        priority: 'low'
      },
      profileUrl: {
        score: Math.floor(Math.random() * 2) + 8, // 8-10
        maxScore: 10,
        current: "URL is clean but could be more memorable and brand-consistent",
        optimized: "Optimize URL to use full name or name + brand keyword. Keep under 25 characters, easy to spell, and consistent across platforms.",
        priority: 'low'
      },
      educationAndCertification: {
        score: hasScrapedData && scrapedData.education && scrapedData.education.length > 0 ? 
          Math.floor(Math.random() * 2) + 7 : Math.floor(Math.random() * 3) + 6,
        maxScore: 10,
        current: hasScrapedData && scrapedData.education && scrapedData.education.length > 0 ? 
          `Found ${scrapedData.education.length} education entries. Latest: ${scrapedData.education[0]?.title || 'N/A'}` :
          "Education listed but lacks honors/leadership details and current certifications",
        optimized: "Include all relevant degrees with honors noted. Add current certifications with validity dates and multimedia proof where appropriate.",
        priority: 'medium'
      }
    };
  };

  const generateAuditResults = (data) => {
    // Get scraped profile data if available
    const generateUrlKey = btoa(profileUrl).replace(/[^a-zA-Z0-9]/g, '');
    const scrapedData = scrapedProfileData || 
                       getCachedScrapedData(profileUrl) ||
                       JSON.parse(sessionStorage.getItem(`scrapedProfileData_${generateUrlKey}`) || 'null');
    
    // Use scraped data to enhance audit results
    const sections = generateSectionsFromScrapedData(scrapedData, data);

    // Calculate overall score
    const totalScore = Object.values(sections).reduce((sum, section) => sum + section.score, 0);
    
    const results = {
      overallScore: totalScore,
      maxScore: 100,
      sections,
      prioritizedActions: [
        {
          title: 'Rewrite Headline with Value Proposition',
          description: 'Create a compelling 220-character headline that communicates who you help, problems you solve, and quantified results you deliver.',
          timeEstimate: '20 minutes',
          priority: 'high',
          category: 'headline',
          impact: '+2-3 points'
        },
        {
          title: 'Restructure About Section',
          description: 'Implement 3-part structure: compelling opener, proof-rich body, and clear call-to-action. Keep under 2,000 characters.',
          timeEstimate: '45 minutes',
          priority: 'high',
          category: 'about',
          impact: '+3-4 points'
        },
        {
          title: 'Quantify Experience Outcomes',
          description: 'Add benefit-driven summaries with quantified results to each role. Use strong action verbs and include proof attachments.',
          timeEstimate: '60 minutes',
          priority: 'high',
          category: 'experience',
          impact: '+2-3 points'
        },
        {
          title: 'Secure Recent Recommendations',
          description: 'Request 5+ specific recommendations from diverse stakeholders focusing on measurable impact and leadership qualities.',
          timeEstimate: '2 weeks',
          priority: 'medium',
          category: 'recommendations',
          impact: '+3-5 points'
        },
        {
          title: 'Optimize Profile Visual Elements',
          description: 'Update profile picture and banner to reflect executive presence with professional quality and brand consistency.',
          timeEstimate: '30 minutes',
          priority: 'medium',
          category: 'visuals',
          impact: '+2-3 points'
        }
      ],
      weeklyPlan: [
        {
          week: 1,
          focus: 'Core Profile Elements',
          tasks: [
            'Rewrite headline with value proposition',
            'Restructure About section (3-part format)',
            'Update profile picture and banner',
            'Optimize LinkedIn URL'
          ],
          expectedGain: '+8-12 points'
        },
        {
          week: 2,
          focus: 'Experience & Credibility',
          tasks: [
            'Add quantified outcomes to experience',
            'Include media attachments as proof',
            'Update education with honors/certifications',
            'List relevant awards and achievements'
          ],
          expectedGain: '+6-10 points'
        },
        {
          week: 3,
          focus: 'Social Proof & Recommendations',
          tasks: [
            'Request recommendations from key stakeholders',
            'Give recommendations to build reciprocity',
            'Ensure tone consistency across all sections',
            'Add contact information and CTAs'
          ],
          expectedGain: '+4-8 points'
        },
        {
          week: 4,
          focus: 'Final Optimization & Monitoring',
          tasks: [
            'Review and polish all content',
            'Test profile from target audience perspective',
            'Set up profile performance tracking',
            'Begin strategic content posting'
          ],
          expectedGain: '+2-4 points'
        }
      ],
      
      insights: {
        strengths: [
          'Professional background is substantial',
          'Current role aligns with target positioning',
          'Network connections are relevant to industry'
        ],
        opportunities: [
          'Headline lacks clear value proposition',
          'About section needs strategic restructuring', 
          'Experience descriptions need quantified outcomes',
          'Missing recent recommendations from key stakeholders'
        ],
        benchmarks: {
          industryAverage: 72,
          topPerformers: 85,
          yourScore: totalScore
        }
      }
    };
    
    // Store results
    sessionStorage.setItem(`auditResults_${generateUrlKey}`, JSON.stringify(results));
    sessionStorage.setItem(`questionnaireData_${generateUrlKey}`, JSON.stringify(data));
    
    setAuditResults(results);
  };

  // One-time coercion of legacy string-shaped opt*/sug* into arrays.
  // The old brain (pre-2026-04-21) emitted one big string with "- " bullet
  // prefixes and newlines between bullets. When we fetch those stored
  // analyses from MongoDB we convert them to arrays ONCE here, so the
  // Dashboard and the PDF only ever see the new array shape and never have
  // to split on render. This is a deterministic read of a well-defined
  // legacy format — not a heuristic regex pass on live text.
  const legacyBulletStringToArray = (text) => {
    if (typeof text !== 'string') return text;
    if (!text.trim()) return [];
    // Split only on line breaks. Strip a single leading "- " / "* " / "• " /
    // "1. " from each resulting line. Nothing else is touched.
    return text
      .split(/\n+/)
      .map((line) => line
        .replace(/^\s*[-*•◦▪]\s+/, '')
        .replace(/^\s*\d+[.)]\s+/, '')
        .trim()
      )
      .filter((line) => line.length > 0);
  };

  // Build results object from AI output while preserving existing UI shape and content
  const buildResultsFromAi = (ai, scrapedData, qData) => {
    try {
      const baseSections = generateSectionsFromScrapedData(scrapedData, qData);

      const breakdown = ai?.breakdown || {};
      const optimizations = ai?.optimizations || {};
      const suggestions = ai?.suggestions || {};


      // Map backend AI keys (breakdown/optimizations/suggestions) to our section keys.
      // The brain emits:
      //   - breakdown.scoreXxx: integer 0-10
      //   - optimizations.optXxx: ARRAY of strings (one bullet per element)
      //   - suggestions.sugXxx:  ARRAY of strings, EXCEPT sugAbout which is a single string
      // New audits come in as arrays already. Legacy stored audits come in
      // as strings — legacyBulletStringToArray() converts those once here
      // so Dashboard always sees arrays. sugAbout stays as string.
      const applyAll = (key, scoreKey, optKey, sugKey) => {
        if (!baseSections[key]) return;

        if (typeof breakdown[scoreKey] === 'number') {
          baseSections[key].score = breakdown[scoreKey];
        }

        const optVal = optimizations[optKey];
        if (Array.isArray(optVal) && optVal.length > 0) {
          baseSections[key].optimized = optVal.filter((s) => typeof s === 'string' && s.trim().length > 0);
        } else if (typeof optVal === 'string' && optVal.trim()) {
          // Legacy: convert "- one\n- two" → ["one", "two"] once.
          const asArray = legacyBulletStringToArray(optVal);
          baseSections[key].optimized = asArray.length > 0 ? asArray : optVal.trim();
        }

        const sugVal = suggestions[sugKey];
        if (Array.isArray(sugVal) && sugVal.length > 0) {
          baseSections[key].suggestions = sugVal.filter((s) => typeof s === 'string' && s.trim().length > 0);
        } else if (typeof sugVal === 'string' && sugVal.trim()) {
          // sugAbout is the only string-typed field by design — keep as string.
          if (sugKey === 'sugAbout') {
            baseSections[key].suggestions = sugVal.trim();
          } else {
            const asArray = legacyBulletStringToArray(sugVal);
            baseSections[key].suggestions = asArray.length > 0 ? asArray : sugVal.trim();
          }
        }
      };

      applyAll('profilePicture', 'scorePfp', 'optPfp', 'sugPfp');
      applyAll('banner', 'scoreBan', 'optBan', 'sugBan');
      applyAll('headline', 'scoreHead', 'optHead', 'sugHead');
      applyAll('about', 'scoreAbout', 'optAbout', 'sugAbout');
      applyAll('profileUrl', 'scoreURL', 'optURL', 'sugURL');
      applyAll('toneAndCohesion', 'scoreToco', 'optToco', 'sugToco');
      applyAll('recommendations', 'scoreReco', 'optReco', 'sugReco');
      applyAll('experience', 'scoreExp', 'optExp', 'sugExp');
      applyAll('honorsAndAwards', 'scoreHonandawar', 'optHonandawar', 'sugHonandawar');
      applyAll('educationAndCertification', 'scoreEdunc', 'optEdunc', 'sugEdunc');

      const totalScore = typeof ai?.overallScore === 'number'
        ? ai.overallScore
        : Object.values(baseSections).reduce((sum, s) => sum + (s?.score || 0), 0);

      const results = {
        overallScore: totalScore,
        maxScore: 100,
        sections: baseSections,
        meta: {
          model: ai?.model || null
        },
        // Include raw scraped data for Dashboard to access
        // NORMALIZE field names: title->school, institute_logo_url->school_logo_url
        education: (() => {
          const edu = scrapedData?.education || [];
          return edu.map(item => ({
            ...item,
            school: item.school || item.title || '',
            school_logo_url: item.school_logo_url || item.institute_logo_url || ''
          }));
        })(),
        experience: scrapedData?.experience || [],
        certifications: scrapedData?.certifications || [],
        skills: scrapedData?.skills || [],
        languages: scrapedData?.languages || [],
        volunteering: scrapedData?.volunteering || scrapedData?.['volunteering-experiences'] || [],
        recommendations: scrapedData?.recommendations?.received_recommendations || scrapedData?.recommendations || [],
        honors_and_awards: scrapedData?.honors_and_awards || scrapedData?.honorsAndAwards || [],
        featured: scrapedData?.featured || [],
        about: scrapedData?.about || scrapedData?.basic_info?.about || '',
        avatar: scrapedData?.avatar || scrapedData?.basic_info?.profile_picture_url || '',
        banner_image: scrapedData?.banner_image || scrapedData?.basic_info?.background_picture_url || '',
        headline: scrapedData?.headline || scrapedData?.basic_info?.headline || '',
        url: scrapedData?.profileUrl || scrapedData?.url || scrapedData?.basic_info?.profile_url || '',
        prioritizedActions: [
          {
            title: 'Rewrite Headline with Value Proposition',
            description: 'Create a compelling 220-character headline that communicates who you help, problems you solve, and quantified results you deliver.',
            timeEstimate: '20 minutes',
            priority: 'high',
            category: 'headline',
            impact: '+2-3 points'
          },
          {
            title: 'Restructure About Section',
            description: 'Implement 3-part structure: compelling opener, proof-rich body, and clear call-to-action. Keep under 2,000 characters.',
            timeEstimate: '45 minutes',
            priority: 'high',
            category: 'about',
            impact: '+3-4 points'
          },
          {
            title: 'Quantify Experience Outcomes',
            description: 'Add benefit-driven summaries with quantified results to each role. Use strong action verbs and include proof attachments.',
            timeEstimate: '60 minutes',
            priority: 'high',
            category: 'experience',
            impact: '+2-3 points'
          },
          {
            title: 'Secure Recent Recommendations',
            description: 'Request 5+ specific recommendations from diverse stakeholders focusing on measurable impact and leadership qualities.',
            timeEstimate: '2 weeks',
            priority: 'medium',
            category: 'recommendations',
            impact: '+3-5 points'
          },
          {
            title: 'Optimize Profile Visual Elements',
            description: 'Update profile picture and banner to reflect executive presence with professional quality and brand consistency.',
            timeEstimate: '30 minutes',
            priority: 'medium',
            category: 'visuals',
            impact: '+2-3 points'
          }
        ],
        weeklyPlan: [
          { week: 1, focus: 'Core Profile Elements', tasks: [
            'Rewrite headline with value proposition',
            'Restructure About section (3-part format)',
            'Update profile picture and banner',
            'Optimize LinkedIn URL'
          ], expectedGain: '+8-12 points' },
          { week: 2, focus: 'Experience & Credibility', tasks: [
            'Add quantified outcomes to experience',
            'Include media attachments as proof',
            'Update education with honors/certifications',
            'List relevant awards and achievements'
          ], expectedGain: '+6-10 points' },
          { week: 3, focus: 'Social Proof & Recommendations', tasks: [
            'Request recommendations from key stakeholders',
            'Give recommendations to build reciprocity',
            'Ensure tone consistency across all sections',
            'Add contact information and CTAs'
          ], expectedGain: '+4-8 points' },
          { week: 4, focus: 'Final Optimization & Monitoring', tasks: [
            'Review and polish all content',
            'Test profile from target audience perspective',
            'Set up profile performance tracking',
            'Begin strategic content posting'
          ], expectedGain: '+2-4 points' }
        ],
        insights: {
          strengths: [
            'Professional background is substantial',
            'Current role aligns with target positioning',
            'Network connections are relevant to industry'
          ],
          opportunities: [
            'Headline lacks clear value proposition',
            'About section needs strategic restructuring',
            'Experience descriptions need quantified outcomes',
            'Missing recent recommendations from key stakeholders'
          ],
          benchmarks: {
            industryAverage: 72,
            topPerformers: 85,
            yourScore: totalScore
          }
        }
      };

      return results;
    } catch (e) {
      console.error('Failed to build results from AI output. Falling back.', e);
      // Fallback to local generation if mapping fails
      const fallbackData = qData || {};
      generateAuditResults(fallbackData);
      const fallbackUrlKey = btoa(profileUrl || sessionStorage.getItem('currentProfileUrl') || '').replace(/[^a-zA-Z0-9]/g, '');
      return JSON.parse(sessionStorage.getItem(`auditResults_${fallbackUrlKey}`) || 'null');
    }
  };

  const pageVariants = {
    initial: { opacity: 0, x: 50 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -50 }
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.5
  };
  const instantTransition = { type: 'tween', ease: 'linear', duration: 0 };

  // Optimizer Flow Component - Start directly with ProfileInput
  const OptimizerFlow = () => {
    // Set initial step to 'profile' instead of 'hero'
    useEffect(() => {
      if (currentStep === 'hero') {
        setCurrentStep('profile');
      }
    }, []);

    return (
      <div className="App">
        <AnimatePresence mode="wait">
          {currentStep === 'profile' && (
          <motion.div
            key="profile"
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={instantTransition}
          >
            <SEO 
              title="Enter Your LinkedIn Profile | LinkedIn Profile Optimizer"
              description="Enter your LinkedIn profile URL to get started with AI-powered analysis and personalized optimization recommendations for your executive profile."
              canonical="https://optimizer.growleads.io/profile"
            />
            <ProfileInput 
              onSubmitUrl={(url) => handleUrlSubmit(url)}
              onEditPrevious={(url) => handleEditPrevious(url)}
              onBack={() => window.location.href = '/'}
            />
          </motion.div>
        )}
        
        {currentStep === 'questionnaire' && (
          <motion.div
            key="questionnaire"
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
          >
            <SEO 
              title="Profile Questionnaire | LinkedIn Profile Optimizer"
              description="Answer a few questions about your professional goals and target audience to receive personalized LinkedIn optimization recommendations."
              canonical="https://optimizer.growleads.io/questionnaire"
              type="article"
            />
            <Questionnaire 
              onComplete={handleQuestionnaireComplete} 
              profileUrl={profileUrl}
              prefill={prefillAnswers}
              onBack={() => setCurrentStep('profile')}
            />
          </motion.div>
        )}
        
        {currentStep === 'loading' && (
          <motion.div
            key="loading"
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
          >
            <SEO 
              title="Analyzing Your Profile | LinkedIn Profile Optimizer"
              description="Analyzing your LinkedIn profile with AI-powered insights. Please wait while we generate your comprehensive profile audit and recommendations."
              canonical="https://optimizer.growleads.io/analyzing"
            />
            <Loading 
              checkScrapingStatus={checkScrapingStatus}
              onComplete={handleLoadingComplete}
            />
          </motion.div>
        )}

        {currentStep === 'dashboard' && (
          // AuthGate WRAPS the motion.div (not inside it). Putting AuthGate
          // INSIDE motion.div caused the AuthGate overlay to inherit
          // motion.div's opacity:0 animation initial state — sign-in card
          // never became visible (the blank-page bug reported 2026-05-18).
          // With AuthGate outside, the gate renders at full opacity
          // regardless of any page-transition animation underneath.
          <AuthGate>
            <motion.div
              key="dashboard"
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
            >
              <SEO
                title={`LinkedIn Profile Analysis Results | LinkedIn Profile Optimizer`}
                description="View your comprehensive LinkedIn profile analysis with personalized recommendations, optimization strategies, and actionable insights to enhance your executive presence."
                canonical="https://optimizer.growleads.io/dashboard"
                type="article"
              />
              <Dashboard
                results={auditResults}
                profileUrl={profileUrl}
                questionnaireData={questionnaireData}
                onBackToHero={() => window.location.href = '/'}
              />
            </motion.div>
          </AuthGate>
        )}
      </AnimatePresence>
    </div>
    );
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/blog" element={<BlogPage />} />
        {/* /optimize is public - URL paste, questionnaire, and AI audit run
            anonymously. AuthGate fires only when transitioning to the Dashboard
            (after the AI score is ready), capturing email at peak intent. */}
        <Route path="/optimize" element={<OptimizerFlow />} />
        {/* Sample audits - fully-realistic example dashboards for SEO/marketing.
            No auth, no paywall. Three personas: founder, fractional-cmo, job-seeker. */}
        <Route path="/sample-audit/:id" element={<SampleAuditViewer />} />
        {/* Any unknown path (/features, /privacy, /terms, /dashboard, /profile, …)
            redirects to the home page so users never land on a blank screen. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
